import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Syringe } from 'lucide-react';
import type { IpdOrderView } from '@his/shared';
import { tr } from '@/lib/i18n-core';
import { get, post } from '@/lib/api';
import { describeError, useAuth } from '@/lib/auth';
import { Dialog } from '@/components/ui/layout';
import { Alert, Button, DateTimePicker, Field, Input, Select, Textarea } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';
import { givenAtFrom, localDateTimeValue } from '../../dose-timing';
import { EXTRA_DOSE_REASON_MIN, extraDoseDefaults, extraDoseProblems, orderDetailParts } from './order-list';

interface Witness {
  id: string;
  name: string;
  designation: string | null;
}

export function ExtraDoseDialog({ order, onClose, onSaved }: { order: IpdOrderView; onClose: () => void; onSaved: () => void }) {
  const toast = useToast();
  const { user } = useAuth();
  const defaults = extraDoseDefaults(order);
  const [openedAt] = useState(() => new Date());
  const [atValue, setAtValue] = useState(() => localDateTimeValue(openedAt));
  const [reason, setReason] = useState(defaults.reason);
  const [doseGiven, setDoseGiven] = useState(defaults.doseGiven);
  const [site, setSite] = useState('');
  const [witnessId, setWitnessId] = useState('');
  const [tried, setTried] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const nurses = useQuery({
    queryKey: ['emar-witnesses'],
    queryFn: () => get<Witness[]>('/ipd/emar/witnesses'),
    enabled: order.isHighAlert,
    staleTime: 300_000,
  });
  const witnesses = (nurses.data ?? []).filter((nurse) => nurse.id !== user?.id);

  const at = givenAtFrom(atValue, openedAt);
  const problems = extraDoseProblems({ at, reason, witnessUserId: witnessId }, order.isHighAlert);
  const shown = (problem: (typeof problems)[number]) => tried && problems.includes(problem);

  const save = useMutation({
    mutationFn: () =>
      post(`/ipd/orders/${order.id}/doses`, {
        at: at.toISOString(),
        reason: reason.trim(),
        doseGiven: doseGiven.trim() || undefined,
        site: site.trim() || undefined,
        witnessUserId: order.isHighAlert ? witnessId : undefined,
      }),
    onSuccess: () => {
      toast.success(tr('ipd', 'dose_recorded'));
      onSaved();
    },
    onError: (caught) => setError(describeError(caught)),
  });

  const submit = () => {
    setTried(true);
    setError(null);
    if (problems.length > 0 || save.isPending) return;
    save.mutate();
  };

  const close = () => {
    if (!save.isPending) onClose();
  };

  return (
    <Dialog
      open
      onClose={close}
      icon={Syringe}
      title={tr('ipd', 'io_ord_dose_title_x', { label: order.label })}
      description={[orderDetailParts(order).join(' · '), tr('ipd', 'io_ord_dose_desc')].filter(Boolean).join(' — ')}
      dirty={reason !== defaults.reason || site !== '' || witnessId !== '' || doseGiven !== defaults.doseGiven}
      footer={
        <>
          <Button variant="outline" onClick={close} disabled={save.isPending}>
            {tr('ipd', 'cancel')}
          </Button>
          <Button onClick={submit} loading={save.isPending}>
            {tr('ipd', 'record')}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {error && <Alert variant="destructive">{error}</Alert>}
        {order.isHighAlert && <Alert variant="warning">{tr('ipd', 'high_alert_medicine_a_second_nurse_must_witness')}</Alert>}

        <Field label={tr('ipd', 'given_at')} required error={shown('future') ? tr('ipd', 'io_ord_dose_in_future') : undefined}>
          <DateTimePicker className="w-full" value={atValue} onChange={setAtValue} max={localDateTimeValue(new Date())} />
        </Field>

        <Field
          label={tr('ipd', 'io_ord_dose_reason')}
          required
          hint={tr('ipd', 'io_ord_reason_min', { min: EXTRA_DOSE_REASON_MIN })}
          hintBelow
          error={shown('reason') ? tr('ipd', 'io_ord_reason_min', { min: EXTRA_DOSE_REASON_MIN }) : undefined}
        >
          <Textarea rows={2} maxLength={400} value={reason} onChange={(event) => setReason(event.target.value)} />
        </Field>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label={tr('ipd', 'io_ord_dose_given')}>
            <Input value={doseGiven} maxLength={80} onChange={(event) => setDoseGiven(event.target.value)} />
          </Field>
          <Field label={tr('ipd', 'site')}>
            <Input value={site} maxLength={80} onChange={(event) => setSite(event.target.value)} placeholder={tr('ipd', 'left_forearm_right_deltoid')} />
          </Field>
        </div>

        {order.isHighAlert && (
          <Field label={tr('ipd', 'witnessed_by')} required error={shown('witness') ? tr('ipd', 'io_ord_witness_required') : undefined}>
            <Select value={witnessId} onChange={(event) => setWitnessId(event.target.value)}>
              <option value="">{tr('ipd', 'select_the_second_nurse')}</option>
              {witnesses.map((nurse) => (
                <option key={nurse.id} value={nurse.id}>
                  {nurse.designation ? `${nurse.name} · ${nurse.designation}` : nurse.name}
                </option>
              ))}
            </Select>
            {nurses.isSuccess && witnesses.length === 0 && <p className="mt-1 text-xs text-destructive">{tr('ipd', 'nobody_else_can_witness_this_dose')}</p>}
          </Field>
        )}
      </div>
    </Dialog>
  );
}
